import React from "react";

import { useLazyQuery, useMutation } from "@apollo/client";
import { Button, Icon, Typography } from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { useLocation, useNavigate } from "react-router-dom";

import CustomSnackBar from "../../Components/CustomSnackBar/CustomSnackBar";
import CustomTextInput from "../../Components/CustomTextInput/CustomTextInput";
import Loading from "../../Components/Loading/Loading";
import {
  Mutation,
  MutationResetPasswordArgs,
  Query,
  QueryGeneratorOtpArgs,
} from "../../graphql/__generated__/types";
import { RESET_PASSWORD } from "../../graphql/resetPassword/mutation";
import { GENERATOR_OTP } from "../../graphql/resetPassword/query";

interface InputFormValues {
  otp: string;
  password: string;
  confirmPassword: string;
}

const defaultValues: InputFormValues = {
  otp: "",
  confirmPassword: "",
  password: "",
};

const ResetPassword: React.FC = () => {
  const {
    state: { idUser: id, email },
  } = useLocation();

  const navigate = useNavigate();
  const [success, setSuccess] = React.useState<boolean>(false);
  const [notice, setNotice] = React.useState<string>("");

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    getValues,
    setValue,
  } = useForm<InputFormValues>();
  const [DoResetPassword, { loading }] = useMutation<
    Mutation,
    MutationResetPasswordArgs
  >(RESET_PASSWORD, {
    onCompleted: (data) => {
      if (data.resetPassword?.id) {
        reset(defaultValues);
        setNotice("Votre mot de passe a été réinitialisé.");
        setSuccess(true);
      }
    },
  });

  // Le formulaire etait seulement vide apres une reinitialisation reussie :
  // l'utilisateur restait sur l'ecran sans savoir si son mot de passe avait
  // change. On laisse la confirmation visible un court instant, puis on le
  // ramene sur l'ecran de connexion, seule suite possible du parcours.
  React.useEffect(() => {
    if (!success) {
      return undefined;
    }
    const timer = setTimeout(() => navigate("/"), 2000);
    return () => clearTimeout(timer);
  }, [success, navigate]);

  // Le code expire au bout de 5 minutes. Sans ce renvoi, l'utilisateur devait
  // repasser par l'ecran "mot de passe oublie" et ressaisir son adresse pour en
  // obtenir un nouveau. "network-only" est indispensable : la query est en
  // "cache-first" par defaut, et un second appel avec la meme adresse serait
  // servi par le cache, sans qu'aucun code ne soit reellement renvoye.
  const [ResendOtp, { loading: resending }] = useLazyQuery<
    Query,
    QueryGeneratorOtpArgs
  >(GENERATOR_OTP, {
    fetchPolicy: "network-only",
  });

  const handleResendCode = async () => {
    const { data } = await ResendOtp({ variables: { input: { email } } });
    if (data?.GeneratorOtp?.id) {
      // Le code precedent n'est plus valable : on vide le champ pour eviter que
      // l'utilisateur ne revalide celui qu'il avait deja saisi.
      setValue("otp", "");
      setNotice("Un nouveau code vous a été envoyé par e-mail.");
    }
  };

  const handleResetPassword = (data: InputFormValues) => {
    const { otp, password } = data;
    DoResetPassword({
      variables: {
        input: {
          id: Number(id),
          otp,
          password,
        },
      },
    });
  };

  return (
    <div
      style={{
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <Loading loading={loading} />
      <CustomSnackBar
        open={!!notice}
        setOpen={() => setNotice("")}
        severity="success"
        message={notice}
      />
      <Icon title="unlock" color="error" />
      <Typography>Réinitialiser votre mot de passe ?</Typography>
      <Typography>Veuillez saisir votre nouveau mot de passe</Typography>
      <Controller
        control={control}
        render={({ field: { onChange, value } }) => (
          <CustomTextInput
            id="otp *"
            label="Code de reinitialisation *"
            fullWidth={false}
            style={{ width: "50%" }}
            onChange={onChange}
            value={value}
            helperText={errors.otp?.message}
            error={!!errors.otp}
          />
        )}
        name="otp"
        rules={{
          maxLength: {
            value: 6,
            message: "Ce champ doit contenir 6 caracteres",
          },
          minLength: {
            value: 6,
            message: "Ce champ doit contenir 6 caracteres",
          },
          required: "Ce champ est requis .",
        }}
        defaultValue=""
      />
      <Controller
        control={control}
        render={({ field: { onChange, value } }) => (
          <CustomTextInput
            id="newpass *"
            label="Nouveau mot de passe"
            type="password"
            fullWidth={false}
            style={{ width: "50%" }}
            onChange={onChange}
            value={value}
            helperText={errors.password?.message}
            error={!!errors.password}
          />
        )}
        name="password"
        rules={{
          required: "Le mot de passe est requis",
        }}
        defaultValue=""
      />

      <Controller
        control={control}
        render={({ field: { onChange, value } }) => (
          <CustomTextInput
            id="otp *"
            label="Confirmation de mot de passe"
            fullWidth={false}
            style={{ width: "50%" }}
            type="password"
            onChange={onChange}
            value={value}
            helperText={errors.confirmPassword?.message}
            error={!!errors.confirmPassword}
          />
        )}
        name="confirmPassword"
        rules={{
          validate: (value) =>
            value === getValues("password") ||
            "Les mots de passe ne correspondent pas",
        }}
        defaultValue=""
      />

      <div
        style={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "center",
          marginTop: "10px",
        }}
      >
        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmit(handleResetPassword)}
        >
          Reinitialiser
        </Button>
        {email ? (
          <Button
            variant="text"
            color="primary"
            onClick={handleResendCode}
            disabled={resending}
            style={{ textTransform: "none", marginLeft: "10px" }}
          >
            {resending ? "Envoi en cours..." : "Renvoyer le code"}
          </Button>
        ) : null}
      </div>
    </div>
  );
};

export default ResetPassword;
